// components/AddressField.jsx
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { FaTrash } from "react-icons/fa";
import { MdOutlineAdd } from "react-icons/md";
import InputMask from "react-input-mask";
import Select from "react-select";
import countryList from "react-select-country-list";

import {
  addressTypeOptions,
  usStateOptions,
} from "../../../../utils/case/usStateOption";
import { selectStyles } from "../../../../utils/selectStyel";

export default function AddressField({
  index,
  field,
  register,
  watch,
  setValue,
  remove,
  verifying,
  suggested,
  onUseSuggested,
  addresses,
  countyOptionsMap,
}) {
  const countryOptions = useMemo(() => countryList().getData(), []);

  return (
    <Box borderWidth="1px" p={2} borderRadius="md" mb={2}>
      <Box display="flex" alignItems="center" justifyContent="end">
        <FaTrash onClick={() => remove(index)} cursor="pointer" color="red" />
      </Box>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
        <FormControl>
          <FormLabel size="sm" mb={0} color="gray.600" fontWeight="normal">
            TYPE
          </FormLabel>
          <Select
            options={addressTypeOptions}
            isClearable
            styles={selectStyles}
            value={
              addressTypeOptions.find(
                (opt) =>
                  opt.value === watch(`ADDRESS.${index}.TYPE`)?.toUpperCase()
              ) || null
            }
            onChange={(selected) =>
              setValue(`ADDRESS.${index}.TYPE`, selected?.value || "")
            }
          />
        </FormControl>
        {[
          ["Street 1", `ADDRESS.${index}.STREET_1`],
          ["Street 2", `ADDRESS.${index}.STREET_2`],
          ["City", `ADDRESS.${index}.CITY`],
        ].map(([label, name]) => (
          <FormControl key={name}>
            <FormLabel size="sm" mb={0} color="gray.600" fontWeight="normal">
              {label}
            </FormLabel>
            <Input size="sm" borderRadius="md" {...register(name)} />
          </FormControl>
        ))}

        <FormControl>
          <FormLabel size="sm" mb={0} color="gray.600" fontWeight="normal">
            State
          </FormLabel>
          <Select
            options={usStateOptions}
            isClearable
            styles={selectStyles}
            value={
              usStateOptions.find(
                (opt) =>
                  opt.value === watch(`ADDRESS.${index}.STATE`)?.toUpperCase()
              ) || null
            }
            onChange={(selected) =>
              setValue(`ADDRESS.${index}.STATE`, selected?.value || "")
            }
          />
        </FormControl>
        {["COUNTY", "ZIP", "COUNTRY"].map((label) => {
          if (label === "COUNTRY") {
            return (
              <FormControl>
                <FormLabel
                  size="sm"
                  mb={0}
                  color="gray.600"
                  fontWeight="normal"
                >
                  {label}
                </FormLabel>
                <Select
                  options={countryOptions}
                  isClearable
                  styles={selectStyles}
                  value={
                    countryOptions.find(
                      (opt) =>
                        opt.value ===
                        watch(`ADDRESS.${index}.COUNTRY`)?.toUpperCase()
                    ) || null
                  }
                  onChange={(selected) =>
                    setValue(`ADDRESS.${index}.COUNTRY`, selected?.value || "")
                  }
                />
              </FormControl>
            );
          }
          if (label === "COUNTY") {
            const selectedState = addresses?.[index]?.STATE;
            const countyOptions = countyOptionsMap[selectedState] || [];
            return (
              <FormControl>
                <FormLabel
                  size="sm"
                  mb={0}
                  color="gray.600"
                  fontWeight="normal"
                >
                  {label}
                </FormLabel>
                <Select
                  options={countyOptions}
                  isClearable
                  styles={selectStyles}
                  value={
                    countyOptions.find(
                      (opt) => opt.value === addresses?.[index]?.COUNTY
                    ) || null
                  }
                  onChange={(selected) =>
                    setValue(`ADDRESS.${index}.COUNTY`, selected?.value || "")
                  }
                />
              </FormControl>
            );
          }
          return (
            <FormControl key={label}>
              <FormLabel size="sm" mb={0} color="gray.600" fontWeight="normal">
                {label}
              </FormLabel>
              <Input
                size="sm"
                borderRadius="md"
                {...register(`ADDRESS.${index}.${label}`)}
              />
            </FormControl>
          );
        })}

        {[
          ["Telephone (Home)", "TELEPHONE_HOME"],
          ["Telephone (Mobile)", "TELEPHONE_MOBILE"],
        ].map(([label, name]) => (
          <FormControl key={name}>
            <FormLabel size="sm" mb={0} color="gray.600" fontWeight="normal">
              {label}
            </FormLabel>
            <InputMask
              mask="(999) 999-9999"
              {...register(`ADDRESS.${index}.${name}`)}
            >
              {(inputProps) => (
                <Input size="sm" borderRadius="md" {...inputProps} />
              )}
            </InputMask>
          </FormControl>
        ))}
        <FormControl>
          <FormLabel size="sm" mb={0} color="gray.600" fontWeight="normal">
            Telephone (Work)
          </FormLabel>
          <Flex align="center" gap={1}>
            <InputMask
              mask="(999) 999-9999"
              {...register(`ADDRESS.${index}.TELEPHONE_WORK`)}
            >
              {(inputProps) => (
                <Input size="sm" borderRadius="md" {...inputProps} />
              )}
            </InputMask>
            <Input
              size="sm"
              borderRadius="md"
              placeholder="Ext."
              {...register(`ADDRESS.${index}.TELEPHONE_WORK_EXT`)}
            />
          </Flex>
        </FormControl>
      </SimpleGrid>

      {/* Suggested address */}
      {verifying ? (
        <Flex mt={3} justify="center">
          <Spinner size="sm" />
        </Flex>
      ) : (
        suggested && (
          <Box
            mt={2}
            p={2}
            bg="gray.100"
            borderRadius="md"
            display="flex"
            alignItems="center"
            gap={2}
            justifyContent="space-between"
          >
            <Box>
              <Text fontSize="sm" fontWeight="bold">
                Suggested Address:
              </Text>
              <Text fontSize="sm">
                {suggested.STREET1}, {suggested.STREET2}, {suggested.CITY},{" "}
                {suggested.STATE} {suggested.ZIP}
              </Text>
            </Box>
            <MdOutlineAdd
              fontWeight={600}
              fontSize={32}
              cursor="pointer"
              onClick={() => onUseSuggested(index)}
            />
          </Box>
        )
      )}
    </Box>
  );
}
